export type TimeLanguage = 'en' | 'th';

export const DEFAULT_DISPLAY_TIME_ZONE = 'Asia/Bangkok';

export interface FormattedTimestamp {
  readonly display: string;
  readonly originalOffset: string;
}

const dateLocale: Record<TimeLanguage, string> = {
  en: 'en-GB-u-ca-gregory-nu-latn',
  th: 'th-TH-u-ca-gregory-nu-latn',
};

const offsetTimestampPattern =
  /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})T(?<hour>\d{2}):(?<minute>\d{2}):(?<second>\d{2})(?:\.\d+)?(?<offset>Z|[+-]\d{2}:\d{2})$/;

const daysByMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

export function formatTimestamp(
  timestamp: string,
  displayTimeZone: string = DEFAULT_DISPLAY_TIME_ZONE,
  language: TimeLanguage = 'en',
): FormattedTimestamp {
  const { instant, originalOffset } = parseOffsetTimestamp(timestamp);

  const parts = new Intl.DateTimeFormat(dateLocale[language], {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: 'short',
    timeZone: displayTimeZone,
    year: 'numeric',
  }).formatToParts(instant);

  const part = (type: Intl.DateTimeFormatPartTypes): string => {
    const value = parts.find((candidate) => candidate.type === type)?.value;
    if (value === undefined) throw new RangeError(`Timestamp formatter omitted ${type}`);
    return value;
  };

  return {
    display: `${part('day')} ${part('month')} ${part('year')}, ${part('hour')}:${part('minute')}`,
    originalOffset,
  };
}

function parseOffsetTimestamp(timestamp: string): {
  readonly instant: Date;
  readonly originalOffset: string;
} {
  const groups = offsetTimestampPattern.exec(timestamp)?.groups;
  if (groups === undefined) throw new RangeError('Timestamp must be strict offset-bearing ISO');

  const year = requiredNumber(groups['year']);
  const month = requiredNumber(groups['month']);
  const day = requiredNumber(groups['day']);
  const hour = requiredNumber(groups['hour']);
  const minute = requiredNumber(groups['minute']);
  const second = requiredNumber(groups['second']);
  const offset = groups['offset'];
  if (offset === undefined) throw new RangeError('Timestamp must include its original UTC offset');

  const maximumDay = daysInMonth(year, month);
  if (maximumDay === undefined || day < 1 || day > maximumDay) {
    throw new RangeError('Timestamp contains an invalid calendar date');
  }
  if (hour > 23 || minute > 59 || second > 59) {
    throw new RangeError('Timestamp contains an invalid time');
  }

  if (offset !== 'Z') {
    const offsetHour = Number(offset.slice(1, 3));
    const offsetMinute = Number(offset.slice(4, 6));
    if (offsetHour > 23 || offsetMinute > 59) {
      throw new RangeError('Timestamp contains an invalid UTC offset');
    }
  }

  const instant = new Date(timestamp);
  if (!Number.isFinite(instant.getTime()))
    throw new RangeError('Timestamp must be a valid instant');

  return { instant, originalOffset: offset === 'Z' ? '+00:00' : offset };
}

function requiredNumber(value: string | undefined): number {
  if (value === undefined) throw new RangeError('Timestamp is missing a required ISO component');
  return Number(value);
}

function daysInMonth(year: number, month: number): number | undefined {
  if (month < 1 || month > 12) return undefined;
  if (month === 2 && isLeapYear(year)) return 29;
  return daysByMonth[month - 1];
}

function isLeapYear(year: number): boolean {
  if (year % 4 !== 0) return false;
  if (year % 100 !== 0) return true;
  return year % 400 === 0;
}
